import axiosInstance from "@/lib/axiosInstance";
import { PLATFORM_TENANT_ID } from "@/store/entityTenantStore";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import type { RoleLevel, RoleScopeKinds } from "@/api/access/access.types";
import { permissionsEndpoints } from "./roles-permissions.endpoints";

/** One role as a company holds it: each entity has its own copy of a role, with its own id. */
export type CompanyRole = {
  id: string;
  name: string;
  code: string | null;
  level: RoleLevel;
  scopeKinds: RoleScopeKinds | null;
  tenantId: string;
};

/** The backend's maximum page size; one page covers every role a company has today. */
const ROLES_PAGE_SIZE = 500;

const PROTECTED_ROLE_CODE = "entity_admin";
const PROTECTED_ROLE_NAMES = new Set(["super admin", "admin"]);

/**
 * A role only a super admin or that entity's Entity Admin may give or take away (backend
 * `isProtectedRole` with the access engine on: Entity Admin by its code, so a renamed one stays
 * protected; the other protected roles by name).
 */
export const isProtectedRole = (role: Pick<CompanyRole, "code" | "name">): boolean =>
  role.code === PROTECTED_ROLE_CODE || PROTECTED_ROLE_NAMES.has(role.name.trim().toLowerCase());

/**
 * The roles of one company: ServEngine's own (`null`) or an entity's copies. The roles list
 * answers for the company the request acts in, so an entity's copies come back only when the
 * request names that entity (`acting-entity-id`); naming ServEngine stops the sidebar's entity
 * pick from being used instead.
 */
export const fetchRolesIn = async (
  entityId: string | null,
  signal?: AbortSignal,
): Promise<CompanyRole[]> => {
  const { data } = await axiosInstance.get(permissionsEndpoints.getAllRoles, {
    params: { page: 1, pageSize: ROLES_PAGE_SIZE },
    actingEntityId: entityId ?? PLATFORM_TENANT_ID,
    suppressErrorAlert: true,
    signal,
  } as CustomAxiosRequestConfig);
  const roles: Array<Partial<CompanyRole> & { status?: string }> = Array.isArray(data?.data)
    ? data.data
    : [];
  return roles
    .filter((role) => role.id && role.name && role.level && role.status !== "inactive")
    .map((role) => ({
      id: role.id!,
      name: role.name!,
      code: role.code ?? null,
      level: role.level!,
      scopeKinds: role.scopeKinds ?? null,
      tenantId: role.tenantId ?? entityId ?? PLATFORM_TENANT_ID,
    }));
};
